<script setup lang="ts">
const { $twMerge } = useNuxtApp();

type Props = {
  delay?: number;       /* ms delay before animation starts (default 0) */
  duration?: number;    /* animation duration in ms (default 600) */
  threshold?: number;   /* IntersectionObserver threshold (default 0.15) */
  distance?: number;    /* Y-axis translate distance in px (default 20) */
  direction?: 'up' | 'down' | 'left' | 'right'; /* animation direction (default 'up') */
  once?: boolean;       /* fire only once (default true) */
  appendClass?: string; /* extra classes on the wrapper */
};

const props = withDefaults(defineProps<Props>(), {
  delay: 0,
  duration: 600,
  threshold: 0.15,
  distance: 20,
  direction: 'up',
  once: true,
  appendClass: '',
});

const { el, isVisible } = useRevealAnimation({ threshold: props.threshold, once: props.once });

const directionMap = {
  up:   { hidden: `translateY(${props.distance}px)`, visible: 'translateY(0)' },
  down: { hidden: `translateY(-${props.distance}px)`, visible: 'translateY(0)' },
  left: { hidden: `translateX(${props.distance}px)`, visible: 'translateX(0)' },
  right:{ hidden: `translateX(-${props.distance}px)`, visible: 'translateX(0)' },
};

const { hidden, visible } = directionMap[props.direction];

const classes = computed(() =>
  $twMerge(
    'opacity-0 transition-all ease-out',
    hidden,
    isVisible.value ? 'opacity-100' : '',
    isVisible.value ? visible : '',
    props.appendClass
  )
);
</script>

<template>
  <div
    ref="el"
    :class="classes"
    :style="{
      transitionDuration: `${props.duration}ms`,
      transitionDelay: `${props.delay}ms`,
    }"
  >
    <slot />
  </div>
</template>
